import type {UseComboboxSelectedItemChange} from 'downshift';
import {CloudDownload, Database, Trash} from 'lucide-react';
import type {MouseEvent} from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import type {ComboboxProps} from '@/components/ui/combobox';
import {Combobox} from '@/components/ui/combobox';
import {IconButton} from '@/components/ui/icon-button';
import {TooltipWrapper} from '@/components/ui/tooltip';

import {
  CREATE_NEW_MEDICATION_VALUE,
  type MedicationOption,
  normalizeMedicationLabel,
  useMedicationInputOptions,
} from '../hooks/useMedicationInputOptions';

export type MedicationInputProps = Omit<
  ComboboxProps<MedicationOption>,
  'items' | 'getGroupLabel'
> & {
  value: string;
  onChange: (value: string) => void;
  onProductIdChange?: (productId: string | undefined) => void;
  initialProductId?: string;
};

function shouldShowCommonName(option: MedicationOption) {
  if (option.type !== 'product' || !option.commonName) return false;

  return (
    normalizeMedicationLabel(option.commonName) !==
    normalizeMedicationLabel(option.productName ?? option.label)
  );
}

function MedicationInput({
  value,
  onChange,
  onProductIdChange,
  initialProductId,
  ...props
}: MedicationInputProps) {
  const {t} = useTranslation();
  const [inputValue, setInputValue] = useState(value ?? '');

  const {
    createMedicationName,
    deleteMedicationName,
    isLoading,
    items,
    cancelDebounce,
  } = useMedicationInputOptions(inputValue, value, initialProductId);

  const selectedOption = useMemo(() => {
    if (initialProductId) {
      return (
        items.find(
          item => item.type === 'product' && item.productId === initialProductId
        ) ?? null
      );
    }

    const normalizedValue = normalizeMedicationLabel(value);

    if (!normalizedValue) return null;

    return (
      items.find(
        item =>
          item.type !== 'create' &&
          normalizeMedicationLabel(item.label) === normalizedValue
      ) ?? null
    );
  }, [initialProductId, items, value]);

  useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.label);
      return;
    }

    setInputValue(value ?? '');
  }, [selectedOption, value]);

  const handleDeleteItem = useCallback(
    (event: MouseEvent<HTMLButtonElement>, item: MedicationOption) => {
      event.preventDefault();
      event.stopPropagation();
      if (item.type !== 'local') return;

      deleteMedicationName.mutate({id: item.value});
      if (item.label !== value) return;

      setInputValue('');
      onChange('');
    },
    [deleteMedicationName, onChange, value]
  );

  const handleSelectedItemChange = useCallback(
    async ({selectedItem}: UseComboboxSelectedItemChange<MedicationOption>) => {
      if (!selectedItem) return;

      cancelDebounce();

      if (selectedItem.value === CREATE_NEW_MEDICATION_VALUE) {
        const data = await createMedicationName.mutateAsync({
          name: selectedItem.label,
        });

        setInputValue(data.createMedicationName.name);
        onChange(data.createMedicationName.name);
        return;
      }

      setInputValue(selectedItem.label);
      onChange(selectedItem.label);

      if (selectedItem.type === 'product') {
        onProductIdChange?.(selectedItem.productId);
      }
    },
    [cancelDebounce, createMedicationName, onChange, onProductIdChange]
  );

  const handleInputValueChange = useCallback(
    ({inputValue: newInputValue}: {inputValue: string}) => {
      const nextValue = newInputValue ?? '';
      if (nextValue === inputValue) return;

      setInputValue(nextValue);

      if (!nextValue) {
        onChange('');
        return;
      }

      onChange(nextValue);
    },
    [inputValue, onChange]
  );

  const getGroupLabel = useCallback(
    (option: MedicationOption, index: number, options: MedicationOption[]) => {
      if (options[index - 1]?.type === option.type) return null;

      switch (option.type) {
        case 'create':
          return t('Add custom medication name');
        case 'local':
          return t('Custom medication names');
        case 'product':
          return t('Medicinal product register');
      }
    },
    [t]
  );

  return (
    <Combobox
      isLoading={isLoading}
      inputValue={inputValue}
      items={items}
      filterItem={() => true}
      getGroupLabel={getGroupLabel}
      itemToString={item => item?.label ?? ''}
      selectedItem={null}
      isItemSelected={item =>
        selectedOption?.value === item.value &&
        selectedOption.type === item.type
      }
      onSelectedItemChange={handleSelectedItemChange}
      onInputValueChange={handleInputValueChange}
      renderItem={option => (
        <div className="flex w-full min-w-0 items-center justify-between gap-1.5">
          <span className="min-w-0 flex-1 truncate leading-tight">
            <span className="block truncate text-sm">{option.label}</span>
            {shouldShowCommonName(option) && (
              <span className="block truncate text-2xs leading-tight text-muted-foreground">
                {option.commonName}
              </span>
            )}
          </span>
          <div className="flex shrink-0 items-center gap-0.5">
            {option.type === 'product' && (
              <TooltipWrapper title={t('Medicinal product register')}>
                <span className="inline-flex size-5 items-center justify-center text-muted-foreground">
                  <CloudDownload className="size-4" />
                </span>
              </TooltipWrapper>
            )}
            {option.type === 'local' && (
              <TooltipWrapper title={t('Custom medication names')}>
                <span className="inline-flex size-5 items-center justify-center text-muted-foreground">
                  <Database className="size-4" />
                </span>
              </TooltipWrapper>
            )}
            {option.type === 'local' && (
              <IconButton
                variant="warning"
                size="small"
                type="button"
                onClick={event => handleDeleteItem(event, option)}
              >
                <span className="sr-only">{t('Delete')}</span>
                <Trash />
              </IconButton>
            )}
          </div>
        </div>
      )}
      {...props}
    />
  );
}

export default MedicationInput;
